<template>
    <div class="lic-setting">
        <h3>설정</h3>
        <p class="muted">팀 및 라이선스를 관리합니다.</p>
        
        <div class="crew-name-setting">
            <div class="gui-form">
                <div class="row-box">
                    <div class="sel-inp-set">
                        <label>팀 선택:</label>
                        <select v-model.number="state.selectedCrewId" @change="onCrewChange">
                            <option :value="null">팀을 선택하세요</option>
                            <option v-for="crew in state.crews" :key="crew.id" :value="crew.id">
                                {{ crew.crewName }}
                            </option>
                        </select>
                    </div>
                    <div class="btn-set" v-if="state.selectedCrewId">
                        <button 
                            type="button" 
                            class="btn btn-primary"
                            @click="toggleEditMode"
                            :disabled="state.loading.update">
                            {{ state.isEditing ? '취소' : '팀 이름 변경' }}
                        </button>
                    </div>
                </div>
                
                <div class="row-box" v-if="state.isEditing && state.selectedCrewId">
                    <div class="inp-set">
                        <label>팀 이름:</label>
                        <input 
                            type="text" 
                            v-model="state.newCrewName" 
                            placeholder="팀 이름을 입력하세요"
                            @keyup.enter="saveCrewName"
                            @keyup.esc="cancelEdit"
                            ref="crewNameInput"
                        />
                    </div>
                    <div class="btn-set">
                        <button 
                            type="button" 
                            class="btn btn-primary"
                            @click="saveCrewName"
                            :disabled="state.loading.update || !state.newCrewName || state.newCrewName.trim() === ''">
                            저장
                        </button>
                        <button 
                            type="button" 
                            class="btn btn-secondary"
                            @click="cancelEdit"
                            :disabled="state.loading.update">
                            취소
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'licenseSetting',
    data() {
        return {
            state: {
                crews: [],
                selectedCrewId: null,
                isEditing: false,
                newCrewName: '',
                loading: {
                    crews: false,
                    update: false,
                },
            },
        };
    },
    mounted() {
        this.fetchCrews();
    },
    methods: {
        fetchCrews() {
            if (!this.isLogin()) {
                return;
            }
            this.state.loading.crews = true;
            
            this.axiosCaller.get(
                this,
                this.APIs.LICENSE + '/getUserCrewList',
                {},
                (result) => {
                    try {
                        const list = this.getSafeArray(result);
                        this.state.crews = list;
                    } catch (e) {
                        console.error('Error loading crews:', e);
                        this.gritterErr('팀 목록 조회 중 오류가 발생했습니다.');
                    } finally {
                        this.state.loading.crews = false;
                    }
                }
            );
        },
        onCrewChange() {
            if (this.state.isEditing) {
                this.cancelEdit();
            }
        },
        toggleEditMode() {
            if (this.state.isEditing) {
                this.cancelEdit();
            } else {
                const selectedCrew = this.state.crews.find(c => c.id === this.state.selectedCrewId);
                if (selectedCrew) {
                    this.state.newCrewName = selectedCrew.crewName || '';
                    this.state.isEditing = true;
                    this.$nextTick(() => {
                        if (this.$refs.crewNameInput) {
                            this.$refs.crewNameInput.focus();
                        }
                    });
                }
            }
        },
        saveCrewName() {
            if (!this.state.selectedCrewId || !this.state.newCrewName || this.state.newCrewName.trim() === '') {
                return;
            }
            
            const selectedCrew = this.state.crews.find(c => c.id === this.state.selectedCrewId);
            if (!selectedCrew) {
                this.gritterErr('선택된 팀을 찾을 수 없습니다.');
                return;
            }
            
            // 이름이 변경되지 않았으면 저장하지 않음
            if (selectedCrew.crewName === this.state.newCrewName.trim()) {
                this.cancelEdit();
                return;
            }
            
            this.state.loading.update = true;
            
            const payload = {
                id: this.state.selectedCrewId,
                crewName: this.state.newCrewName.trim(),
                ownerEmail: selectedCrew.ownerEmail || '',
            };
            
            this.axiosCaller.post(
                this,
                this.APIs.LICENSE + '/setCrew',
                payload,
                (result) => {
                    try {
                        const data = this.getData(result);
                        if (data?.status === 'ok') {
                            this.gritterOk('팀 이름이 변경되었습니다.');
                            // 팀 목록 새로고침
                            this.fetchCrews();
                            this.state.isEditing = false;
                            this.state.newCrewName = '';
                        } else {
                            this.gritterErr(data?.msg || '팀 이름 변경에 실패했습니다.');
                        }
                    } catch (e) {
                        console.error('Error updating crew name:', e);
                        this.gritterErr('팀 이름 변경 중 오류가 발생했습니다.');
                    } finally {
                        this.state.loading.update = false;
                    }
                }
            );
        },
        cancelEdit() {
            this.state.isEditing = false;
            this.state.newCrewName = '';
        },
        // 공통 유틸
        getData(resOrData) { return resOrData?.data ?? resOrData; },
        getSafeArray(resOrData) {
            const result = this.getData(resOrData);
            if (result?.status && result.status !== 'ok') {
                this.gritterErr(result?.msg || '요청이 실패했습니다.');
                return [];
            }
            const topCandidates = [result?.list, result?.rows, result?.content, result?.data];
            for (const v of topCandidates) if (Array.isArray(v)) return v;
            const nestedCandidates = [result?.data?.list, result?.data?.rows, result?.data?.content];
            for (const v of nestedCandidates) if (Array.isArray(v)) return v;
            if (result && typeof result === 'object') {
                for (const k of Object.keys(result)) if (Array.isArray(result[k])) return result[k];
            }
            return [];
        },
        gritterOk(msg) { 
            this.swalUtils?.gritter 
                ? this.swalUtils.gritter('Success', msg || '성공', 'success', 2000) 
                : console.log('[OK]', msg); 
        },
        gritterErr(msg) { 
            const m = msg || '정상적인 접근이 아닙니다. 다시 시도해주세요.'; 
            this.swalUtils?.gritter 
                ? this.swalUtils.gritter('error', m, 'error', 2000) 
                : console.error('[ERR]', m); 
        },
    },
};
</script>

<style scoped>
.lic-setting {
    padding: 20px;
}

.muted { 
    color: #666; 
    margin-bottom: 30px;
}

.crew-name-setting {
    margin-top: 30px;
    padding: 20px;
    background: #f9f9f9;
    border-radius: 8px;
}

.gui-form {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.row-box {
    display: flex;
    align-items: center;
    gap: 15px;
    flex-wrap: wrap;
}

.sel-inp-set,
.inp-set {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 200px;
}

.sel-inp-set label,
.inp-set label {
    min-width: 100px;
    font-weight: 500;
    color: #333;
}

.sel-inp-set select,
.inp-set input {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
}

.sel-inp-set select:focus,
.inp-set input:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.btn-set {
    display: flex;
    gap: 10px;
}

.btn {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-primary {
    background-color: #007bff;
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background-color: #0056b3;
}

.btn-secondary {
    background-color: #6c757d;
    color: white;
}

.btn-secondary:hover:not(:disabled) {
    background-color: #545b62;
}
</style>


